import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useContact, useCreateTemplate, useLayout } from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Save } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { LayoutSelector } from './layout-selector'
import { type TemplateFormData, TemplateFormFields } from './template-form-fields'

/** Mirror of the server-side `slugify` util — kept identical so the
 *  auto-filled slug matches what the server would derive on submit. */
function autoSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

import { TemplatePreview } from './template-preview'
import {
  buildVariablesRecord,
  DEFAULT_STATE,
  TemplateVariables,
  type TemplateVariableState,
} from './template-variables'
export function TemplateNew() {
  const navigate = useNavigate()
  const { perform: createTemplate, loading: saving } = useCreateTemplate()
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    slug: '',
    subject: '',
    body: '',
    mode: 'plaintext',
  })
  // Tracks whether the user has manually edited the slug. Auto-fill on
  // name change keeps suggesting until they take over.
  const [slugEdited, setSlugEdited] = useState(false)
  const [layoutId, setLayoutId] = useState<string | null>(null)
  const [varState, setVarState] = useState<TemplateVariableState>(DEFAULT_STATE)
  const { data: previewContact } = useContact(varState.contactId ?? undefined)
  const { data: layoutDetail } = useLayout(layoutId ?? undefined)
  const variablesRecord = useMemo(
    () => buildVariablesRecord(varState, previewContact ?? null),
    [varState, previewContact],
  )

  const handleFieldChange = useCallback(
    (field: keyof TemplateFormData, value: string) => {
      setFormData((prev) => {
        const next = { ...prev, [field]: value }
        // Auto-derive slug from name until the user manually edits it.
        if (field === 'name' && !slugEdited) {
          next.slug = autoSlug(value)
        }
        if (field === 'slug') {
          setSlugEdited(true)
        }
        return next
      })
    },
    [slugEdited],
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      try {
        const created = await createTemplate({
          name: formData.name,
          slug: formData.slug || undefined,
          subject: formData.subject.trim() || null,
          body: formData.body,
          layoutId,
        })
        navigate(`/templates/${created.id}`, { replace: true })
      } catch (err) {
        // Without this, an unhandled rejection silently kills the redirect
        // and the form just sits there — making it look like the create
        // succeeded when it actually 500'd.
        toast.error('Failed to create template', { description: getApiErrorMessage(err) })
      }
    },
    [formData, layoutId, createTemplate, navigate],
  )

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/templates">Back to Templates</BackLink>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <Card className="min-w-0 flex-1">
          <CardHeader>
            <CardTitle>New Template</CardTitle>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit}>
              <TemplateFormFields data={formData} onChange={handleFieldChange} />
              <div className="mt-4">
                <LayoutSelector value={layoutId} onChange={setLayoutId} />
              </div>
              <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={saving || !formData.name.trim() || !formData.body.trim()}>
                  <Save className="mr-1 h-4 w-4" />
                  {saving ? 'Creating...' : 'Create Template'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <TemplatePreview
            body={formData.body}
            subject={formData.subject}
            mode={formData.mode}
            variables={variablesRecord}
            layoutBody={layoutDetail?.body}
          />
          <TemplateVariables state={varState} onChange={setVarState} templateBody={formData.body} />
        </div>
      </div>
    </Page>
  )
}
